import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useInvoices } from '../../hooks/useInvoices';
import { useProjects } from '../../hooks/useProjects';
import { usePeriods } from '../../hooks/useAdmin';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { BulkMarkPaidPanel } from '../../components/invoices/BulkMarkPaidPanel';
import { formatCurrency, formatDisplayDate, formatDisplayDateTime } from '../../utils/format';
import { hasDerivedDueDate, netTermLabel } from '../../utils/invoiceDueDate';
import type { Invoice, InvoicePaymentStatus } from '../../services/api/invoices';

type SortField = 'name' | 'invoiceNumber' | 'from' | 'to' | 'dueDate' | 'invoicedAmount';
type SortDirection = 'asc' | 'desc';

// MKS-214 — the three payment states, rendered as a badge. Mirrors the
// backend's derived `status`; the page never recomputes it from `dueDate`.
const STATUS_LABELS: Record<InvoicePaymentStatus, string> = {
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  paid: 'Paid',
};

const STATUS_CLASSES: Record<InvoicePaymentStatus, string> = {
  unpaid: 'bg-gray-100 text-gray-700',
  overdue: 'bg-red-100 text-red-800',
  paid: 'bg-green-100 text-green-800',
};

function StatusBadge({ status }: { status: InvoicePaymentStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function isPaymentStatus(value: string): value is InvoicePaymentStatus {
  return value === 'unpaid' || value === 'overdue' || value === 'paid';
}


function SortIcon({ direction, active }: { direction: SortDirection; active: boolean }) {
  if (!active) {
    return (
      <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  if (direction === 'asc') {
    return (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<{ field: SortField; direction: SortDirection }>({ field: 'from', direction: 'desc' });

  // Read filters and pagination from URL
  const projectFilter = searchParams.get('projectId') || '';
  const periodFilter = searchParams.get('periodId') || '';
  const statusParam = searchParams.get('status') || '';
  // Ignore a junk `?status=` left in a bookmarked URL, so the list never
  // silently returns a filtered subset the visible control cannot explain.
  const statusFilter = isPaymentStatus(statusParam) ? statusParam : undefined;
  // MKS-352 — `?deleted=1` is the deletion log.
  const deletedView = searchParams.get('deleted') === '1';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page'); // Reset to page 1 when filters change
    setSearchParams(params, { replace: true });
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    setSearchParams(params, { replace: true });
  };

  // Data
  const { data: invoices, isLoading, error } = useInvoices({
    projectId: projectFilter ? parseInt(projectFilter, 10) : undefined,
    periodId: periodFilter ? parseInt(periodFilter, 10) : undefined,
    // The payment status of a cancelled invoice is meaningless, so the log
    // ignores the filter rather than silently intersecting with it.
    status: deletedView ? undefined : statusFilter,
    deleted: deletedView || undefined,
  });
  const { data: projects } = useProjects({ state: 'in_progress' });
  const { data: periods } = usePeriods();

  const sortedProjects = useMemo(() => {
    if (!projects) return [];
    return [...projects].sort((a, b) => (a.fullName || (a.name ?? '')).localeCompare(b.fullName || (b.name ?? '')));
  }, [projects]);

  // Sorting
  const handleSort = (field: SortField) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedInvoices = useMemo(() => {
    if (!invoices) return [];
    return [...invoices].sort((a, b) => {
      const { field, direction } = sort;
      let aVal: string | number;
      let bVal: string | number;

      switch (field) {
        case 'name':
          aVal = (a.name ?? '').toLowerCase();
          bVal = (b.name ?? '').toLowerCase();
          break;
        case 'invoiceNumber':
          aVal = (a.invoiceNumber ?? '').toLowerCase();
          bVal = (b.invoiceNumber ?? '').toLowerCase();
          break;
        case 'from':
          aVal = a.from ?? '';
          bVal = b.from ?? '';
          break;
        case 'to':
          aVal = a.to ?? '';
          bVal = b.to ?? '';
          break;
        case 'dueDate':
          // MKS-214 — ISO dates sort lexicographically; a null due date sorts
          // as '' so the invoices with no due date cluster at one end instead
          // of interleaving arbitrarily.
          // MKS-454 — a DERIVED due date sorts as the date it is, with the
          // stored ones: the column shows it, and sorting a visible date as
          // '' would scatter the rows the Status column calls overdue.
          aVal = a.dueDate ?? a.effectiveDueDate ?? '';
          bVal = b.dueDate ?? b.effectiveDueDate ?? '';
          break;
        case 'invoicedAmount':
          aVal = a.invoicedAmount;
          bVal = b.invoicedAmount;
          break;
      }

      let comparison: number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = (aVal as number) - (bVal as number);
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [invoices, sort]);

  // Pagination
  const totalPages = Math.ceil(sortedInvoices.length / pageSize);
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedInvoices.slice(start, start + pageSize);
  }, [sortedInvoices, currentPage, pageSize]);

  const renderSortHeader = (label: string, field: SortField, align: 'left' | 'right' = 'left', width?: string) => (
    <th
      onClick={() => handleSort(field)}
      style={width ? { width } : undefined}
      className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none group hover:bg-gray-100 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <SortIcon direction={sort.direction} active={sort.field === field} />
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
        <div className="flex items-center gap-3">
          {/* MKS-349 — the way in to the accounting back-fill. */}
          <Button variant="secondary" onClick={() => navigate('/invoices/import')}>
            Import from accounting
          </Button>
          <Button onClick={() => navigate('/invoices/new')}>
            Generate Invoice
          </Button>
        </div>
      </div>

      {/* MKS-349 follow-up — step one of the accounting back-fill rollout.
          Collapsed to a single ghost button until asked for: it is a
          once-a-year action on a page finance uses daily. */}
      {!deletedView && <BulkMarkPaidPanel />}

      {/* Filters */}
      <div className="flex items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={projectFilter}
            onChange={(e) => setFilter('projectId', e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">- All -</option>
            {sortedProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.fullName || p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <select
            value={periodFilter}
            onChange={(e) => setFilter('periodId', e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">- All -</option>
            {periods?.map((period) => (
              <option key={period.id} value={period.id}>{period.name}</option>
            ))}
          </select>
        </div>
        {/* MKS-352 — the payment status of a cancelled invoice means nothing,
            so the filter goes away in the deletion log rather than sitting
            there filtering nothing. */}
        {!deletedView && (
          <div>
            <label htmlFor="invoice-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="invoice-status-filter"
              value={statusFilter ?? ''}
              onChange={(e) => setFilter('status', e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">- All -</option>
              <option value="overdue">Overdue</option>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        )}
        {/* MKS-352 — the deletion log. A filter rather than its own page: it
            is the same invoices, and finance looks for a cancelled one in the
            place they looked for it before it was cancelled. */}
        <div>
          <label htmlFor="invoice-deleted-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Show
          </label>
          <select
            id="invoice-deleted-filter"
            value={deletedView ? '1' : ''}
            onChange={(e) => setFilter('deleted', e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">Active invoices</option>
            <option value="1">Deleted invoices</option>
          </select>
        </div>
      </div>

      {sortedInvoices.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-gray-500">{deletedView ? 'No deleted invoices.' : 'No invoices found.'}</p>
          <p className="text-sm text-gray-400 mt-1">
            {deletedView
              ? 'Nothing has been deleted yet. An invoice deleted from its detail page appears here with who deleted it, when and why.'
              : 'Adjust filters or generate your first invoice to get started.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Top Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, sortedInvoices.length)}</span> of{' '}
                <span className="font-medium">{sortedInvoices.length}</span> results
              </p>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={`page-${i}`}
                      onClick={() => setPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          )}
          <div className="overflow-x-auto">
          {/* MKS-352 — the deletion log is a different set of columns about
              the same rows: not the period and status finance sorts live
              invoices by, but who removed one, when and why. A separate table
              rather than extra columns on the live one, which already fills
              its width. */}
          {deletedView ? (
            <table className="divide-y divide-gray-200" style={{ width: '100%', tableLayout: 'fixed' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th style={{ width: '22%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th style={{ width: '10%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th style={{ width: '14%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th style={{ width: '10%' }} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th style={{ width: '14%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted by</th>
                  <th style={{ width: '12%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted at</th>
                  <th style={{ width: '18%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedInvoices.map((invoice: Invoice, index: number) => (
                  <tr
                    key={`deleted-invoice-${invoice.id}-${index}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="truncate" title={invoice.name || 'Untitled'}>
                        {invoice.name || 'Untitled'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.invoiceNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.from && invoice.to
                        ? `${formatDisplayDate(invoice.from)} - ${formatDisplayDate(invoice.to)}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(invoice.invoicedAmount)}
                    </td>
                    {/* A deleter whose user row is gone leaves the id behind
                        rather than dropping the row from the log. */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.deletedByName || (invoice.deletedById ? `User #${invoice.deletedById}` : '-')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.deletedAt ? formatDisplayDateTime(invoice.deletedAt) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="truncate" title={invoice.deleteReason ?? ''}>
                        {invoice.deleteReason || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
          <table className="divide-y divide-gray-200" style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead className="bg-gray-50">
              <tr>
                {/* MKS-214 — widths re-balanced to fit Due date and Status.
                    They sum to 100% under tableLayout:fixed. */}
                {renderSortHeader('Name', 'name', 'left', '20%')}
                {renderSortHeader('Invoice #', 'invoiceNumber', 'left', '9%')}
                {renderSortHeader('From', 'from', 'left', '9%')}
                {renderSortHeader('To', 'to', 'left', '9%')}
                {renderSortHeader('Due date', 'dueDate', 'left', '9%')}
                <th style={{ width: '9%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                {renderSortHeader('Amount', 'invoicedAmount', 'right', '9%')}
                {/* MKS-352 — Petra 2026-10-15: a red Delete on every row is
                    one misclick away from destroying an invoice, so deleting
                    happens only from the invoice detail, where it asks for a
                    reason. With no row actions left, Projects takes the old
                    Actions column's width back rather than leaving an empty
                    7% gutter; the widths still sum to 100% under
                    tableLayout:fixed. */}
                <th style={{ width: '26%' }} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedInvoices.map((invoice: Invoice, index: number) => (
                <tr
                  key={`invoice-${invoice.id}-${index}`}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="truncate" title={invoice.name || 'Untitled'}>
                      {invoice.name || 'Untitled'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.invoiceNumber || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDisplayDate(invoice.from)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDisplayDate(invoice.to)}
                  </td>
                  {/* MKS-454 — a derived due date is shown here too, with its
                      term: the Status column next to it now calls such an
                      invoice overdue, and a '-' beside that badge is exactly
                      the contradiction this ticket is removing. */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.dueDate ? (
                      formatDisplayDate(invoice.dueDate)
                    ) : hasDerivedDueDate(invoice) ? (
                      <span title={`Derived from the issue date, ${netTermLabel(invoice.derivedNetDays)}`}>
                        {formatDisplayDate(invoice.effectiveDueDate)}{' '}
                        <span className="text-gray-400">({netTermLabel(invoice.derivedNetDays)})</span>
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <StatusBadge status={invoice.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatCurrency(invoice.invoicedAmount)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" onClick={(e) => e.stopPropagation()}>
                    <div className="truncate" title={invoice.projects?.map((p) => p.name).join(', ') || '-'}>
                      {invoice.projects?.length > 0
                        ? invoice.projects.map((p, i) => (
                            <span key={`proj-${p.id}-${i}`}>
                              {i > 0 && ', '}
                              <Link
                                to={`/projects/${p.id}`}
                                className="text-primary-600 hover:underline"
                              >
                                {p.name}
                              </Link>
                            </span>
                          ))
                        : '-'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-medium">
                {/* MKS-214 — spans every column left of Amount: Name, Invoice
                    #, From, To, Due date and Status. */}
                <td className="px-6 py-3 text-sm text-gray-900" colSpan={6}>
                  Page total ({paginatedInvoices.length} invoices) | All: {sortedInvoices.length} invoices
                </td>
                <td className="px-6 py-3 text-sm text-right font-mono text-gray-900">
                  {formatCurrency(paginatedInvoices.reduce((sum, inv) => sum + inv.invoicedAmount, 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
          )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, sortedInvoices.length)}</span> of{' '}
                    <span className="font-medium">{sortedInvoices.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                      return (
                        <button
                          key={`page-${i}`}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            pageNum === currentPage
                              ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
